import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import { captureError } from '@/lib/sentry';
import { useOrganization } from '@/hooks/useOrganization';
import { useAnalytics } from '@/hooks/useAnalytics';
import { StatusOrdemServico } from '@/types/ordemServico';
import {
  listOrdensServico,
  listOSDisponiveis,
  listOSIndicadorPorCliente,
  getOrdemServicoById,
  autoatribuirOS,
  criarOrdemServico,
  atualizarOrdemServico,
  atualizarStatusOS,
  iniciarExecucaoOS as iniciarExecucaoOSService,
  transferirOS,
  adicionarObservacaoOS,
  reagendarOS,
  excluirOS,
  reabrirOS,
  ReabrirOSInvalidStatusError,
  type FiltrosOS,
  type AtualizarOSInput,
} from '@/services/ordens-servico.service';
import { sendOSTemplate } from '@/services/whatsapp.service';
import { subscribeToOSChanges, unsubscribeChannel } from '@/services/realtime-os.service';

export { parseServicos, parseEndereco } from '@/services/ordens-servico.normalizers';
export type { FiltrosOS };

export function useOrdensServico(filtros?: FiltrosOS) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ['ordens-servico', organizationId, filtros],
    queryFn: () => listOrdensServico(filtros),
    enabled: true,
  });
}

export function useOSDisponiveis(_servicosOferecidos?: string[]) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ['ordens-servico', organizationId, 'disponiveis'],
    queryFn: listOSDisponiveis,
    enabled: true,
    refetchInterval: 30000,
  });
}

export function useClientesOSIndicador(clienteIds: string[] | undefined) {
  const { organizationId } = useOrganization();
  // Chave estável independente da ordem dos ids.
  const sorted = (clienteIds ?? []).slice().sort();
  return useQuery({
    queryKey: ['os-indicador', organizationId, sorted],
    queryFn: () => listOSIndicadorPorCliente(sorted),
    enabled: sorted.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Mantém o indicador da OS no avatar do inbox fresco em tempo real. Subscreve
 * todas as mudanças em ordens_servico e invalida apenas a query do indicador
 * (sem toasts — esses são para o prestador em /meus-servicos). Cobre tanto as
 * ações do atendente como as do prestador (que ocorrem noutro dispositivo).
 */
export function useRealtimeOSIndicador() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const invalidar = () => {
      queryClient.invalidateQueries({ queryKey: ['os-indicador'] });
    };
    const channel = subscribeToOSChanges(
      { onInsert: invalidar, onUpdate: invalidar },
      'os-indicador-realtime',
    );
    return () => unsubscribeChannel(channel);
  }, [queryClient]);
}

export function useAutoatribuirOS() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (osId: string) => {
      try {
        return await autoatribuirOS(osId);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('não está cadastrado')) {
          throw new Error(t('orders.toast.notRegistered'));
        }
if (message.includes('não disponível') || message.includes('já foi aceita')) {
          throw new Error(t('orders.toast.alreadyAccepted'));
        }
        if (message === 'NO_ROW_RETURNED') {
          throw new Error(t('orders.toast.acceptError'));
        }
        throw err;
      }
    },
    onSuccess: (os) => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      toast.success(t('orders.toast.osAccepted', { number: os.numero_os }));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useAutoatribuirOS' } });
      toast.error(error.message);
    },
  });
}

export function useOrdemServico(id: string | undefined) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ['ordem-servico', organizationId, id],
    queryFn: () => (id ? getOrdemServicoById(id) : null),
    enabled: !!id,
  });
}

export function useCriarOrdemServico() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();
  const { track, getUserProperty, setUserProperties } = useAnalytics();

  return useMutation({
    mutationFn: async (dados: Omit<Parameters<typeof criarOrdemServico>[0], 'orgId'>) => {
      return criarOrdemServico({ ...dados, orgId: requireOrgId() });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      if (!getUserProperty('has_created_first_os')) {
        track('activation_first_os_created');
        setUserProperties({ has_created_first_os: true });
      }
      toast.success(t('orders.toast.osCreated', { number: data.numero_os }));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useCriarOrdemServico' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useAtualizarOrdemServico() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: AtualizarOSInput }) =>
      atualizarOrdemServico(id, dados, requireOrgId()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      // A query de detalhe é ['ordem-servico', organizationId, id]; invalidar
      // por prefixo ['ordem-servico'] apanha-a (a variante com variables.id
      // não correspondia por causa do organizationId no meio da chave).
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      // O histórico (useEventosOS) tem de refrescar para mostrar o evento de edição.
      queryClient.invalidateQueries({ queryKey: ['eventos-os'] });
      toast.success(t('orders.toast.osUpdated'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useAtualizarOrdemServico' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useAtualizarStatusOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      id, status, observacao, prestador_id,
    }: {
      id: string;
      status: StatusOrdemServico;
      observacao?: string;
      prestador_id?: string | null;
    }) => atualizarStatusOS({ id, orgId: requireOrgId(), status, observacao, prestador_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      toast.success(t('orders.toast.statusUpdated'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useAtualizarStatusOS' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useAceitarOS() {
  const atualizarStatus = useAtualizarStatusOS();

  return useMutation({
    mutationFn: (id: string) => atualizarStatus.mutateAsync({ id, status: 'aceita' }),
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useAceitarOS' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useRecusarOS() {
  const atualizarStatus = useAtualizarStatusOS();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) =>
      atualizarStatus.mutateAsync({ id, status: 'recusada', observacao: motivo }),
  });
}

export function useIniciarExecucaoOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => iniciarExecucaoOSService({ id, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      toast.success(t('orders.toast.executionStarted'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useIniciarExecucaoOS' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useConcluirOS() {
  const atualizarStatus = useAtualizarStatusOS();

  return useMutation({
    mutationFn: async ({ id, observacao }: { id: string; observacao?: string }) => {
      const result = await atualizarStatus.mutateAsync({ id, status: 'concluida', observacao });
      // Fire-and-forget: notify cliente via WhatsApp template. NEVER blocks the
      // mutation result and NEVER throws (service contract).
      sendOSTemplate(id).catch((err) =>
        console.warn('[useConcluirOS] sendOSTemplate threw unexpectedly', err),
      );
      return result;
    },
  });
}

export function useCancelarOS() {
  const atualizarStatus = useAtualizarStatusOS();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) =>
      atualizarStatus.mutateAsync({ id, status: 'cancelada', observacao: motivo }),
  });
}

export function useTransferirOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      id, prestador_id, responsavel_id,
    }: {
      id: string;
      prestador_id?: string;
      responsavel_id?: string;
    }) => transferirOS({ id, orgId: requireOrgId(), prestador_id, responsavel_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      toast.success(t('orders.toast.transferCompleted'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useTransferirOS' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useAdicionarObservacaoOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      id, observacao, interna,
    }: {
      id: string;
      observacao: string;
      interna?: boolean;
    }) => adicionarObservacaoOS({ id, orgId: requireOrgId(), observacao, interna }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      toast.success(t('orders.toast.noteAdded'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useAdicionarObservacaoOS' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useReagendarOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      id, novaData, motivo,
    }: {
      id: string;
      novaData: string;
      motivo: string;
    }) => reagendarOS({ id, orgId: requireOrgId(), novaData, motivo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      queryClient.invalidateQueries({ queryKey: ['eventos-os'] });
      toast.success(t('orders.toast.osRescheduled'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useReagendarOS' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useExcluirOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      excluirOS({ id, orgId: requireOrgId(), motivo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      toast.success(t('orders.toast.osDeleted'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useExcluirOS' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useReabrirOS() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) =>
      reabrirOS({ id, orgId: requireOrgId(), motivo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      queryClient.invalidateQueries({ queryKey: ['eventos-os'] });
      toast.success(t('orders.toast.osReopened'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useReabrirOS' } });
      if (error instanceof ReabrirOSInvalidStatusError) {
        toast.error(t('orders.toast.onlyCompletedOrCancelled'));
        return;
      }
      toast.error(sanitizeSupabaseError(error));
    },
  });
}
